import { useEffect, useRef } from 'react';
import { usePreferencesStore } from '../../store/preferencesStore';
import { matchesShortcut, isCapturingShortcut } from '../../config/shortcuts';
import { isMacPlatform } from '../../utils/keyboard';
import { togglePalette } from './paletteOpenState';

/**
 * ⌘K / Ctrl+K from everywhere (PLAN D3), and ⌘P / Ctrl+P quick open (D1).
 *
 * ONE listener on `window` in the CAPTURE phase, registered once at app start:
 * the earliest JS point for a keydown, so it runs before Monaco (and its Ctrl+K
 * chord), Lexical, xterm's helper textarea, the chat composer, inputs and any
 * modal's own handlers. On a match: preventDefault + stopImmediatePropagation,
 * so neither the WebView's / browser's Ctrl+K nor an editor chord sees it.
 *
 * Not on the sign-in gate (D5): while `gated`, the key is CONSUMED and does
 * nothing — no store flip that would pop the palette open after sign-in.
 *
 * Opt-out (Windows / Linux): Settings → Terminal → "Send Ctrl+K to the shell".
 * Then Ctrl+K whose target is inside `.xterm` is left alone; ⌘P / Ctrl+P and
 * the title-bar button still open the palette there.
 */
export function paletteKeyHandler({ isGated, getPrefs = () => usePreferencesStore.getState(), toggle = togglePalette, isMac = isMacPlatform }) {
  return (e) => {
    if (isCapturingShortcut()) return;
    if (e.isComposing || e.keyCode === 229) return;
    const prefs = getPrefs();
    const overrides = prefs.keyboardShortcuts;
    const isToggle = matchesShortcut(e, 'commandPalette', overrides);
    const isQuick = !isToggle && matchesShortcut(e, 'quickOpen', overrides);
    if (!isToggle && !isQuick) return;
    if (isToggle && prefs.terminalSendCtrlK === true && !isMac() && e.ctrlKey && !e.metaKey && e.target?.closest?.('.xterm')) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation?.();
    if (e.repeat) return; // a held key toggles once
    if (isGated()) return;
    toggle({ mode: isQuick ? 'quickOpen' : null, at: e.timeStamp });
  };
}

export default function usePaletteShortcut({ gated }) {
  const gatedRef = useRef(gated);
  gatedRef.current = gated;
  useEffect(() => {
    const onKey = paletteKeyHandler({ isGated: () => gatedRef.current });
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);
}
